/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * COLOR SIBLINGS SWATCHES
 * ------------------------------------------------------------------------------------------------------------
 *
 * Renders a list of color swatches (one per sibling product, as configured through the "custom.related_products"
 * product metafield) on the product page. Clicking a swatch loads the selected product without a full page reload,
 * using the Shopify Section Rendering API (https://shopify.dev/docs/api/ajax/section-rendering).
 *
 * Behavior:
 *   1. The whole product section markup is swapped in place, which automatically re-instantiates every custom
 *      element of the theme (variant picker, gallery, buy buttons...) since they are inserted back into the DOM.
 *   2. The currently selected size is preserved. We look up the target product variants and keep the same size
 *      when it exists and is purchasable. Otherwise we fall back to the first available (or first) variant.
 *   3. The URL is updated through the History API with `replaceState`, so the swatch navigation does not pollute
 *      the history (the back button returns to the previous page, e.g. the collection, not the previous color)
 *      while still allowing a refresh to keep the chosen color and size.
 *   4. Sibling swatch hrefs are collection-scoped in Liquid (`within: collection`) when the sibling
 *      belongs to the current collection, so breadcrumbs stay consistent (Home > Collection > Product).
 *   5. In quick-buy drawers/popovers, the drawer content is swapped in place (preserving size) without changing
 *      the page URL.
 */
class SiblingColorSwatches extends HTMLElement {
  connectedCallback() {
    this._loading = false;
    this._abortController = new AbortController();
    this.addEventListener('click', this._onClick.bind(this), { signal: this._abortController.signal });
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  get sectionId() {
    return this.getAttribute('section-id');
  }

  get formId() {
    return this.getAttribute('form-id');
  }

  get isQuickBuy() {
    return this.getAttribute('context') === 'quick-buy';
  }

  get quickBuyHost() {
    return this.closest('quick-buy-drawer, quick-buy-popover');
  }

  get optionRoot() {
    return this.isQuickBuy && this.quickBuyHost ? this.quickBuyHost : document;
  }

  _getProductHandle(pathnameOrUrl) {
    if (window.BoaBreadcrumb?.getProductHandle) {
      return window.BoaBreadcrumb.getProductHandle(pathnameOrUrl);
    }

    const match = String(pathnameOrUrl).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Product JSON lives at `/products/{handle}.js` (not under a collection path).
   */
  _getProductJsonUrl(pathnameOrUrl) {
    const handle = this._getProductHandle(pathnameOrUrl);

    if (!handle) {
      return null;
    }

    const rootUrl = window.themeVariables?.routes?.rootUrl || '/';
    const normalizedRoot = rootUrl.endsWith('/') ? rootUrl.slice(0, -1) : rootUrl;

    return `${normalizedRoot}/products/${handle}.js`;
  }

  _onClick(event) {
    const swatchLink = event.target.closest('.color-swatch__item[data-product-handle]');

    if (!swatchLink || !this.contains(swatchLink)) {
      return;
    }

    // Let modified clicks (open in new tab, etc.) and the no-JS fallback behave like a normal link.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
      return;
    }

    // Sold-out siblings keep a normal link navigation to the product page.
    if (swatchLink.closest('.color-swatch.is-sold-out')) {
      return;
    }

    event.preventDefault();

    if (this._loading || swatchLink.classList.contains('is-selected')) {
      return;
    }

    this._switchToProduct(swatchLink);
  }

  /**
   * Reads the option values of the variant currently selected in the variant picker, indexed by (lowercased)
   * option name. This is used to keep the same size when switching to another color.
   */
  _getSelectedOptions() {
    const selectedOptions = {};
    const variantPicker = this.optionRoot.querySelector(`variant-picker[form-id="${this.formId}"]`);

    if (!variantPicker) {
      return selectedOptions;
    }

    const variantScript = variantPicker.querySelector('script[data-variant]');
    const optionNameElements = variantPicker.querySelectorAll('.product-form__option-name');

    if (!variantScript) {
      return selectedOptions;
    }

    let selectedVariant;

    try {
      selectedVariant = JSON.parse(variantScript.textContent || '{}');
    } catch (e) {
      return selectedOptions;
    }

    if (!selectedVariant || !Array.isArray(selectedVariant.options)) {
      return selectedOptions;
    }

    selectedVariant.options.forEach((value, index) => {
      const nameElement = optionNameElements[index];
      const optionName = nameElement
        ? nameElement.textContent.replace(/[:：]\s*$/, '').trim().toLowerCase()
        : `option${index + 1}`;

      selectedOptions[optionName] = value;
    });

    return selectedOptions;
  }

  /**
   * Picks the best matching variant of the target product given the previously selected options. We only enforce an
   * option (e.g. the size) when the target product actually offers the selected value, which naturally ignores the
   * color option since it differs between siblings.
   */
  _findBestVariant(targetProduct, selectedOptions) {
    const variants = targetProduct.variants || [];

    if (variants.length === 0) {
      return null;
    }

    const optionNames = (targetProduct.options || []).map((option) => {
      return (option && option.name ? option.name : option).toString().toLowerCase();
    });

    const enforcedValues = optionNames.map((name, index) => {
      const desiredValue = selectedOptions[name];

      if (desiredValue == null) {
        return null;
      }

      const targetHasValue = variants.some((variant) => (variant.options || [])[index] === desiredValue);
      return targetHasValue ? desiredValue : null;
    });

    const matchesSelection = (variant) => {
      return enforcedValues.every((value, index) => {
        return value == null || (variant.options || [])[index] === value;
      });
    };

    const availableVariants = variants.filter((variant) => variant.available);

    return (
      availableVariants.find(matchesSelection) ||
      availableVariants[0] ||
      variants.find(matchesSelection) ||
      variants[0]
    );
  }

  async _switchToQuickBuyProduct(swatchLink, targetUrl) {
    const quickBuyHost = this.quickBuyHost;

    if (!quickBuyHost) {
      window.location.href = targetUrl.toString();
      return;
    }

    const response = await fetch(targetUrl.toString(), { signal: this._abortController.signal });

    if (!response.ok) {
      throw new Error(`Unexpected response (${response.status})`);
    }

    const html = document.createRange().createContextualFragment(await response.text());
    const quickBuyTemplate = html.querySelector('#quick-buy-content');

    if (!quickBuyTemplate) {
      throw new Error('Missing quick-buy content');
    }

    const isDrawer = quickBuyHost.tagName.toLowerCase() === 'quick-buy-drawer';
    const sourceElement = quickBuyTemplate.content.querySelector(
      isDrawer ? 'quick-buy-drawer' : 'quick-buy-popover'
    );

    if (!sourceElement) {
      throw new Error('Missing quick-buy markup');
    }

    quickBuyHost.replaceChildren(...sourceElement.children);
    quickBuyHost.setAttribute('href', `${targetUrl.pathname}${targetUrl.search}`);

    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }
  }

  async _switchToProduct(swatchLink) {
    this._loading = true;
    this.setAttribute('aria-busy', 'true');
    this.classList.add('is-loading');

    const hrefProductUrl = swatchLink.getAttribute('href').split('?')[0];
    const productJsonUrl = this._getProductJsonUrl(hrefProductUrl) || `${hrefProductUrl}.js`;
    const selectedOptions = this._getSelectedOptions();

    try {
      // Resolve the variant to keep (preserving the selected size when possible).
      let variantId = null;
      let targetProduct = null;

      try {
        const productResponse = await fetch(productJsonUrl, { signal: this._abortController.signal });

        if (productResponse.ok) {
          targetProduct = await productResponse.json();
          const variant = this._findBestVariant(targetProduct, selectedOptions);
          variantId = variant ? variant.id : null;
        }
      } catch (e) {
        if (e.name === 'AbortError') {
          return;
        }
      }

      const resolvedTargetUrl = window.BoaBreadcrumb?.scopeProductUrl
        ? window.BoaBreadcrumb.scopeProductUrl(new URL(hrefProductUrl, window.location.origin))
        : new URL(hrefProductUrl, window.location.origin);

      if (variantId) {
        resolvedTargetUrl.searchParams.set('variant', variantId);
      }

      if (this.isQuickBuy) {
        try {
          await this._switchToQuickBuyProduct(swatchLink, resolvedTargetUrl);
        } catch (e) {
          if (e.name === 'AbortError') {
            return;
          }

          window.location.href = resolvedTargetUrl.toString();
        }

        return;
      }

      // Fetch the product section in the selected color/variant through the Section Rendering API.
      const sectionUrl = new URL(resolvedTargetUrl.toString());
      sectionUrl.searchParams.set('section_id', this.sectionId);

      let sectionHtml;

      try {
        const sectionResponse = await fetch(sectionUrl.toString(), { signal: this._abortController.signal });

        if (!sectionResponse.ok) {
          throw new Error(`Unexpected response (${sectionResponse.status})`);
        }

        sectionHtml = await sectionResponse.text();
      } catch (e) {
        if (e.name === 'AbortError') {
          return;
        }

        // Network/Section API failure: fall back to a regular navigation so the user is never stuck.
        window.location.href = resolvedTargetUrl.toString();
        return;
      }

      const currentSection = document.getElementById(`shopify-section-${this.sectionId}`);

      if (!currentSection) {
        window.location.href = resolvedTargetUrl.toString();
        return;
      }

      const fragment = document.createRange().createContextualFragment(sectionHtml);
      const incomingSection = fragment.querySelector(`#shopify-section-${this.sectionId}`);

      // Depending on the Shopify response, the section wrapper may or may not be included. Handle both cases so that
      // we never end up with a duplicated/nested section wrapper.
      if (incomingSection) {
        currentSection.replaceWith(incomingSection);
      } else {
        currentSection.innerHTML = '';
        currentSection.appendChild(fragment);
      }

      // Update the URL without adding a history entry (back button still returns to the collection page).
      window.history.replaceState({ ...window.history.state }, '', resolvedTargetUrl.toString());

      if (targetProduct?.title) {
        this._updateDocumentTitle(targetProduct.title);
      }

      // Section HTML may omit the collection crumb (plain product URL / Markets). Re-apply it.
      window.BoaBreadcrumb?.restore?.({ force: true });

      // Re-initialize the dynamic checkout buttons that were re-rendered.
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }

      // Let apps relying on the section lifecycle re-bind to the freshly rendered markup.
      document.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: this.sectionId }
        })
      );
    } finally {
      this._loading = false;
      this.removeAttribute('aria-busy');
      this.classList.remove('is-loading');
    }
  }

  _updateDocumentTitle(productTitle) {
    document.title = productTitle;
  }
}

if (!window.customElements.get('sibling-color-swatches')) {
  window.customElements.define('sibling-color-swatches', SiblingColorSwatches);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * LISTING PAGE SIBLING PRODUCT SWATCHES
 * ------------------------------------------------------------------------------------------------------------
 *
 * Caps visible swatches when data-limit-sibling-swatches is set. Clicks are plain links to the PDP
 * (note: no in-place card swap — metafield order reshuffled on every sibling).
 */
class ProductItemSiblingSwatches extends HTMLElement {
  static SWATCH_LIMIT = 4;

  connectedCallback() {
    this.productItem = this.closest('product-item');
    this._enforceSwatchLimit();
  }

  _enforceSwatchLimit() {
    if (!this.hasAttribute('data-limit-sibling-swatches')) {
      return;
    }

    const list = this.querySelector('.product-item-meta__sibling-swatches-list');

    if (!list) {
      return;
    }

    const swatches = [...list.querySelectorAll(':scope > .color-swatch:not(.color-swatch--more)')];

    if (swatches.length <= ProductItemSiblingSwatches.SWATCH_LIMIT) {
      return;
    }

    swatches.slice(ProductItemSiblingSwatches.SWATCH_LIMIT).forEach((swatch) => swatch.remove());

    if (!list.querySelector('.color-swatch--more')) {
      list.appendChild(this._createMoreSwatch());
    }
  }

  _createMoreSwatch() {
    const productTitle =
      this.productItem?.querySelector('.product-item-meta__title')?.textContent?.trim() || '';
    const productUrl = this.productItem?.querySelector('.product-item-meta__title')?.getAttribute('href') || '';
    const moreSwatch = document.createElement('div');
    const link = document.createElement('a');
    const plus = document.createElement('span');
    const label = document.createElement('span');

    moreSwatch.className = 'color-swatch color-swatch--more';
    link.href = productUrl;
    link.setAttribute('data-instant', '');
    link.className = 'color-swatch__item color-swatch__item--more';
    plus.setAttribute('aria-hidden', 'true');
    plus.textContent = '+';
    label.className = 'visually-hidden';
    label.textContent = productTitle;
    link.append(plus, label);
    moreSwatch.appendChild(link);

    return moreSwatch;
  }
}

if (!window.customElements.get('product-item-sibling-swatches')) {
  window.customElements.define('product-item-sibling-swatches', ProductItemSiblingSwatches);
}
/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT CARD IMAGE CAROUSEL (21/10/2026)
 * ------------------------------------------------------------------------------------------------------------
 *
 * <product-item-carousel> is the horizontal scroll-snap track rendered by snippets/product-item.liquid when
 * `image_carousel` is passed (collection grid). Scrolling itself is native (trackpad / touch swipe); this class only:
 *   1. keeps the dots under the image in sync with the visible slide,
 *   2. upgrades the lazy slides to eager on the first hover / touch so the next image is ready before the swipe,
 *   3. scrolls to the variant image when a color swatch (variant option) inside the card is selected
 *      (the theme's own ProductItem handler still updates the links).
 */
class ProductItemCarousel extends HTMLElement {
  connectedCallback() {
    this._wrapper = this.closest('.product-item__image-wrapper');
    this._dots = this._wrapper ? [...this._wrapper.querySelectorAll('.product-item__dot')] : [];
    this._images = [...this.querySelectorAll('img')];
    this._index = 0;
    this._raf = null;
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    this.addEventListener('scroll', this._onScroll.bind(this), { passive: true, signal });

    const preload = this._preloadSlides.bind(this);
    this.addEventListener('pointerenter', preload, { once: true, signal });
    this.addEventListener('touchstart', preload, { once: true, passive: true, signal });

    const productItem = this.closest('product-item');
    if (productItem) {
      productItem.addEventListener('change', this._onSwatchChanged.bind(this), { signal });
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  get isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  _preloadSlides() {
    this._images.forEach((image) => {
      if (image.getAttribute('loading') === 'lazy') {
        image.setAttribute('loading', 'eager');
      }
    });
  }

  _onScroll() {
    if (this._raf) {
      return;
    }

    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      const width = this.clientWidth || 1;
      // scrollLeft is negative in RTL (standard behavior in current browsers)
      const index = Math.min(this._images.length - 1, Math.round(Math.abs(this.scrollLeft) / width));

      if (index !== this._index) {
        this._setActive(index);
      }
    });
  }

  _setActive(index) {
    this._index = index;
    this._dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
  }

  scrollToIndex(index, behavior = 'smooth') {
    const direction = this.isRtl ? -1 : 1;
    this.scrollTo({ left: direction * index * this.clientWidth, behavior });
  }

  _onSwatchChanged(event) {
    const input = event.target;

    if (!input.matches('.color-swatch__radio') || !input.hasAttribute('data-variant-featured-media')) {
      return;
    }

    const mediaId = input.getAttribute('data-variant-featured-media');
    const index = this._images.findIndex((image) => image.getAttribute('data-media-id') === mediaId);

    if (index >= 0) {
      this.scrollToIndex(index);
    }
  }
}

if (!window.customElements.get('product-item-carousel')) {
  window.customElements.define('product-item-carousel', ProductItemCarousel);
}
